/* eslint eslint-comments/no-use: off */
import {clsx} from 'clsx'
import {useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {ProjectItemSectionFields$key} from './__generated__/ProjectItemSectionFields.graphql'
import type {ProjectItemSectionFieldsValues$key} from './__generated__/ProjectItemSectionFieldsValues.graphql'
import {DateField, type DateFieldFragment} from './fields/DateField'
import {IterationField} from './fields/IterationField'
import {MultiSelectField} from './fields/MultiSelectField'
import {NumberField, type NumberFieldConfigFragment, type NumberFieldFragment} from './fields/NumberField'
import {SingleSelectField} from './fields/SingleSelectField'
import {TextField, type TextFieldFragment} from './fields/TextField'
import styles from './ProjectsSections.module.css'

type ProjectItemSectionFieldsProps = {
  projectItem: ProjectItemSectionFields$key
  onIssueUpdate: () => void
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ProjectItemSectionFieldsFragment = graphql`
  fragment ProjectItemSectionFields on ProjectV2Item {
    id
    isArchived
    project {
      id
      title
      url
      viewerCanUpdate
      fields(first: 50, orderBy: {field: POSITION, direction: ASC}) {
        edges {
          node {
            ... on ProjectV2Field {
              id
              dataType
              name
              isIssueField
            }
            ... on ProjectV2IterationField {
              id
              dataType
              name
              isIssueField
              ...IterationFieldConfigFragment @dangerously_unaliased_fixme
            }
            ... on ProjectV2SingleSelectField {
              id
              dataType
              name
              isIssueField
              ...SingleSelectFieldConfigFragment @dangerously_unaliased_fixme
            }
            ... on ProjectV2MultiSelectField {
              id
              dataType
              name
              isIssueField
              ...MultiSelectFieldConfigFragment @dangerously_unaliased_fixme
            }
          }
        }
      }
    }
    ...ProjectItemSectionFieldsValues
  }
`

export function ProjectItemSectionFields({projectItem, onIssueUpdate}: ProjectItemSectionFieldsProps) {
  const projectItemFields = useFragment(ProjectItemSectionFieldsFragment, projectItem)

  const {fieldValues} = useFragment<ProjectItemSectionFieldsValues$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ProjectItemSectionFieldsValues on ProjectV2Item {
        fieldValues(first: 50, orderBy: {field: POSITION, direction: ASC})
          @connection(key: "ProjectItemSection_fieldValues") {
          edges {
            node {
              ... on ProjectV2ItemFieldTextValue {
                id
                text
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ... on ProjectV2ItemFieldNumberValue {
                id
                number
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ... on ProjectV2ItemFieldDateValue {
                id
                date
                field {
                  ... on ProjectV2Field {
                    id
                  }
                }
              }
              ... on ProjectV2ItemFieldSingleSelectValue {
                id
                field {
                  ... on ProjectV2SingleSelectField {
                    id
                  }
                }
              }
              ...SingleSelectFieldFragment @dangerously_unaliased_fixme
              ... on ProjectV2ItemFieldIterationValue {
                id
                field {
                  ... on ProjectV2IterationField {
                    id
                  }
                }
              }
              ...IterationFieldFragment @dangerously_unaliased_fixme
              ... on ProjectV2ItemFieldMultiSelectValue {
                id
                field {
                  ... on ProjectV2MultiSelectField {
                    id
                  }
                }
              }
              ...MultiSelectFieldFragment @dangerously_unaliased_fixme
            }
          }
        }
      }
    `,
    projectItemFields,
  )

  const fields = useMemo(
    () =>
      (projectItemFields?.project.fields?.edges || [])
        .map(edge => {
          // Issue fields are not rendered in the project item field list until there is a way to edit them and
          // improved GraphQL support.
          if (!edge?.node || edge.node.isIssueField || edge.node.name === 'Status') {
            return
          }

          const field = edge.node
          const dataType = field.dataType

          const fieldId = field.id
          const value =
            (fieldValues?.edges || []).flatMap(e => (e?.node ? [e.node] : [])).find(e => e?.field?.id === fieldId) ||
            null

          const props = {
            viewerCanUpdate: !projectItemFields.isArchived && projectItemFields.project.viewerCanUpdate,
            itemId: projectItemFields.id,
            projectId: projectItemFields.project.id,
            field,
            value,
            onIssueUpdate,
          }

          switch (dataType) {
            case 'SINGLE_SELECT':
              return <SingleSelectField key={fieldId} {...props} />
            case 'TEXT':
              // RELAY-CLEANUP(ProjectItemSectionFields): value is optional/widened while the parent is
              // Relay-owned. Remove when ProjectItemSectionFields leaves Relay.
              return <TextField key={fieldId} {...props} value={value as TextFieldFragment | null} />
            case 'DATE':
              // RELAY-CLEANUP(ProjectItemSectionFields): value is optional/widened while the parent is
              // Relay-owned. Remove when ProjectItemSectionFields leaves Relay.
              return <DateField key={fieldId} {...props} value={value as DateFieldFragment | null} />
            case 'NUMBER':
              // RELAY-CLEANUP(ProjectItemSectionFields): id/name remain optional and the value is narrowed
              // from the still-Relay parent data. Remove when ProjectItemSectionFields leaves Relay.
              return (
                <NumberField
                  key={fieldId}
                  {...props}
                  field={field as NumberFieldConfigFragment}
                  value={value as NumberFieldFragment | null}
                />
              )
            case 'ITERATION':
              return <IterationField key={fieldId} {...props} />
            case 'MULTI_SELECT': {
              return <MultiSelectField key={fieldId} {...props} />
            }
          }
        })
        .filter(i => i !== undefined),
    [
      projectItemFields.project.fields?.edges,
      projectItemFields.project.viewerCanUpdate,
      projectItemFields.project.id,
      projectItemFields.isArchived,
      projectItemFields.id,
      fieldValues?.edges,
      onIssueUpdate,
    ],
  )

  return fields.length === 0 ? (
    <> </>
  ) : (
    <ul className={clsx(styles.FieldListUl, styles.FieldListInternal)}>
      {fields.map((field, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <li className={styles.FieldListLi} key={`project-field-${index}`}>
          {field}
        </li>
      ))}
    </ul>
  )
}
